import { Children, useState, type ReactNode } from 'react';
import { ListFilter } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { Buscador } from './buscador';
import { FilterChips, type FilterChipData } from './filter-chips';

export interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Filter controls, right-aligned. */
  children?: ReactNode;
  chips?: readonly FilterChipData[];
  onRemoveChip?: (chip: FilterChipData) => void;
  onClear?: () => void;
  /** Delay before `onSearchChange` fires; 0 makes the field fully controlled. */
  searchDelay?: number;
  /**
   * Cómo se llama el campo para un lector de pantalla, cuando el `placeholder`
   * no alcanza. Ver `Buscador`.
   */
  searchLabel?: string;
  /** Hay una consulta en vuelo con el término actual. */
  searching?: boolean;
  /** Mínimo de caracteres antes de salir a preguntar. Ver `useBusqueda`. */
  searchMin?: number;
  /**
   * Esconde el campo de búsqueda y deja sólo los filtros y los chips.
   *
   * Para las listas cuyo servidor no sabe buscar. Un campo que filtrara lo que
   * se alcanzó a bajar diría que buscó en todo, que es el dato exacto que
   * contesta otra pregunta; y uno deshabilitado sería un control muerto en el
   * lugar más visible de la barra. La barra sigue sirviendo por lo demás: los
   * chips son lo que deja quitar un filtro que llegó en un enlace.
   */
  hideSearch?: boolean;
  /**
   * GUARDA LOS FILTROS DETRÁS DE UN SOLO BOTÓN «Filtros».
   *
   * Apagado —lo que hacen hoy Contactos, Leads y Negocios— cada faceta es un
   * botón suelto en el renglón. Encendido, todas se van adentro de un panel y
   * en la barra queda el buscador y un botón con cuántas están puestas.
   *
   * Es una opción y no el comportamiento nuevo de todos a propósito: lo pidió
   * Empresas, que es la lista con más facetas —ocho— y la única donde no
   * entraban en el renglón. Cambiarle el aspecto de la barra a las otras cuatro
   * pantallas en la misma PR sería otra decisión, y no es la que se tomó.
   */
  panel?: boolean;
  className?: string;
}

/**
 * The toolbar every list view wears: search on the left, filters on the right,
 * and what is currently filtering spelled out underneath.
 *
 * El campo ya no vive acá: es `Buscador`, el mismo que usan el tablero de
 * Empresas, la mesa de ayuda, la cola de Escalamiento y la bandeja de Correo.
 * Esta barra sólo decide dónde ponerlo. Lo que se ganó al sacarlo es que las
 * cuatro pantallas que lo tenían escrito a mano dejaran de comportarse cada una
 * a su manera — ver el encabezado de `useBusqueda`.
 */
export function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'Buscar…',
  children,
  chips = [],
  onRemoveChip,
  onClear,
  searchDelay,
  searchLabel,
  searching = false,
  searchMin,
  hideSearch = false,
  panel = false,
  className,
}: FilterBarProps) {
  const isActive = chips.length > 0 || search.length > 0;
  // `Children.toArray` tira los `null`, los `undefined` y los booleanos, así
  // que un `{!board && (<>…</>)}` que no dibujó nada cuenta cero. Sin esto el
  // tablero de Empresas —que pide la barra sin facetas— estrenaría un botón
  // «Filtros» que abre un panel vacío.
  const hayFiltros = Children.toArray(children).length > 0;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap items-start gap-2">
        {!hideSearch && (
          <Buscador
            valor={search}
            onCambiar={onSearchChange}
            placeholder={searchPlaceholder}
            buscando={searching}
            className="flex-1 sm:max-w-80"
            {...(searchLabel ? { etiqueta: searchLabel } : {})}
            {...(searchDelay === undefined ? {} : { retardo: searchDelay })}
            {...(searchMin === undefined ? {} : { minimo: searchMin })}
          />
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {panel && hayFiltros ? (
            <PanelDeFiltros
              count={chips.length}
              canClear={isActive}
              {...(onClear ? { onClear } : {})}
            >
              {children}
            </PanelDeFiltros>
          ) : (
            children
          )}
        </div>
      </div>

      {/* Only chips open this strip. With just a search term active it used to
          render a lone «Limpiar» floating under an empty row — a second, more
          prominent way to clear a field that already carries its own ✕.

          CON EL PANEL SE QUEDAN IGUAL, y son la mitad que no se puede reemplazar
          por el número del botón: el número dice CUÁNTOS filtros hay puestos y
          el chip dice CUÁL es cada uno, que es lo que alguien necesita para
          entender por qué la lista trae lo que trae sin abrir nada. Los dos
          cuentan lo mismo —hay un chip por filtro— así que nunca se contradicen,
          y quitar uno solo sigue siendo un clic sin pasar por el panel. */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {onRemoveChip && <FilterChips chips={chips} onRemove={onRemoveChip} />}
          {isActive && onClear && (
            <Button variant="ghost" size="sm" onClick={onClear} className="h-7 px-2 text-xs">
              Limpiar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

interface PanelDeFiltrosProps {
  /** Cuántos filtros están puestos: el número del botón. */
  count: number;
  /**
   * Hay algo que limpiar. No es `count > 0`: `onClear` también borra el término
   * de búsqueda —lo hace desde siempre, es el mismo `clearFilters` que usa el
   * «Limpiar» de los chips— y un botón apagado teniendo algo escrito en el
   * buscador se leería como que no hace nada.
   */
  canClear: boolean;
  onClear?: () => void;
  children: ReactNode;
}

/**
 * Los filtros de la barra, todos adentro de un panel.
 *
 * Apila lo que le pasen y no sabe qué es: los controles siguen siendo los de la
 * pantalla, y cada uno sigue leyendo y escribiendo la URL como siempre, así que
 * el enlace compartido, el botón atrás y lo que se le pide al servidor no
 * cambian en nada.
 *
 * Lo que SÍ conviene pasarle son facetas que se abran en el sitio
 * (`FilterSection`) y no desplegables. Un `FilterSelect` acá adentro funciona
 * —Radix apila las capas bien: propaga los eventos por el árbol de React, así
 * que un clic en la faceta no le llega al panel como «clic afuera», y `Escape`
 * cierra primero la de arriba— pero su lista de opciones cae encima del panel y
 * tapa a las demás facetas, que es justo lo que el panel venía a resolver.
 */
function PanelDeFiltros({ count, canClear, onClear, children }: PanelDeFiltrosProps) {
  const [abierto, setAbierto] = useState(false);

  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          // El mismo traje que las facetas que guarda: punteado mientras no
          // filtra nada, sólido cuando sí. Es la señal que ya usa `FilterSelect`.
          className={cn('gap-1.5 border-dashed', count > 0 && 'border-solid')}
        >
          <ListFilter className="size-3.5 text-muted-foreground" aria-hidden />
          Filtros
          {count > 0 && (
            <>
              <Separator orientation="vertical" className="mx-0.5 h-4" />
              <Badge variant="secondary" className="tabular">
                {count}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>

      {/* `align="end"` porque el botón vive pegado al borde derecho de la barra:
          alineado al principio, el panel se salía de la pantalla. */}
      <PopoverContent align="end" aria-label="Filtros" className="w-72 p-2">
        {/* Scrollea: con las ocho facetas abiertas el panel sería más alto que
            la pantalla, y el «Limpiar todo» de abajo quedaría fuera de alcance.
            `70vh` deja ver que hay barra abajo sin comerse el viewport. */}
        <div className="flex max-h-[70vh] flex-col gap-1.5 overflow-y-auto scrollbar-thin">
          {children}
        </div>

        {onClear && (
          <div className="mt-2 border-t border-border pt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={!canClear}
              onClick={() => {
                onClear();
                // Se cierra: quien limpia todo ya terminó con el panel, y
                // dejarlo abierto sobre ocho facetas en blanco parece que el
                // botón no hizo nada.
                setAbierto(false);
              }}
              className="w-full justify-center"
            >
              Limpiar todo
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
